/**
 * Rekonstruktion des Bildes aus Kachel-Atlanten.
 *
 * Das Verfahren entspricht old/dcp-vue/src/components/avView.vue: Ein
 * Offscreen-Canvas in Sendeauflösung hält das Vollbild, unveränderte Bereiche
 * bleiben schlicht stehen. Zwei Unterschiede zum Original:
 *
 *  - Die Kacheln kommen aus einem dicht gepackten Atlas statt aus einem
 *    bildschirmgroßen Bild, dessen unveränderte Teile weiß sind. Das Original
 *    musste diese per clearRect wegschneiden; hier wird gezielt kopiert.
 *  - Die Ausgabe berücksichtigt devicePixelRatio, sonst wirkt das Bild auf
 *    HiDPI-Displays unnötig weich.
 */

import { TILE_DIM, countTilesX } from '../../shared/constants.ts';
import { atlasCols, cellContentOrigin, tilePosition } from '../../shared/atlasLayout.ts';
import { FRAME_DIFF, type Frame } from '../../shared/protocol.ts';

export class Renderer {
  private full: OffscreenCanvas | null = null;
  private fullCtx: OffscreenCanvasRenderingContext2D | null = null;
  private ctx: CanvasRenderingContext2D;

  private width = 0;
  private height = 0;
  private tilesX = 0;

  /** Aspect-Fit: Zielrechteck des Bildes im sichtbaren Canvas. */
  private fit = { x: 0, y: 0, width: 0, height: 0 };

  constructor(
    private canvas: HTMLCanvasElement,
    private container: HTMLElement,
  ) {
    this.ctx = canvas.getContext('2d')!;
    this.resize();
  }

  get hasImage(): boolean {
    return this.full !== null;
  }

  /** Legt die Sendeauflösung fest; ein vorhandenes Bild geht dabei verloren. */
  setGeometry(width: number, height: number): void {
    if (this.width === width && this.height === height && this.full) return;
    this.width = width;
    this.height = height;
    this.tilesX = countTilesX(width);
    this.full = new OffscreenCanvas(width, height);
    this.fullCtx = this.full.getContext('2d')!;
    this.fullCtx.fillStyle = '#000';
    this.fullCtx.fillRect(0, 0, width, height);
    this.computeFit();
  }

  async apply(frame: Frame): Promise<void> {
    if (frame.type === FRAME_DIFF) {
      if (!this.full) return;
      await Promise.all([
        this.blit(frame.lossyData, frame.lossyMime, frame.lossyTiles),
        this.blit(frame.losslessData, frame.losslessMime, frame.losslessTiles),
      ]);
    } else {
      this.setGeometry(frame.width, frame.height);
      const bitmap = await createImageBitmap(new Blob([frame.data as BlobPart], { type: frame.mime }));
      this.fullCtx!.drawImage(bitmap, 0, 0);
      bitmap.close();
    }
    this.present();
  }

  /** Kopiert die Nutzflächen aus einem Atlas an ihre Zielpositionen. */
  private async blit(data: Uint8Array | null, mime: string, tiles: Uint16Array): Promise<void> {
    if (!data || tiles.length === 0 || !this.fullCtx) return;

    const bitmap = await createImageBitmap(new Blob([data as BlobPart], { type: mime }));
    const cols = atlasCols(tiles.length);

    for (let cell = 0; cell < tiles.length; cell++) {
      const src = cellContentOrigin(cell, cols);
      const dst = tilePosition(tiles[cell]!, this.tilesX);

      // Am rechten und unteren Rand ist die letzte Kachel angeschnitten.
      const w = Math.min(TILE_DIM, this.width - dst.x);
      const h = Math.min(TILE_DIM, this.height - dst.y);
      if (w <= 0 || h <= 0) continue;

      this.fullCtx.drawImage(bitmap, src.x, src.y, w, h, dst.x, dst.y, w, h);
    }
    bitmap.close();
  }

  /** Passt das sichtbare Canvas an die Containergröße an. */
  resize(): void {
    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(this.container.clientWidth * dpr));
    const height = Math.max(1, Math.round(this.container.clientHeight * dpr));
    if (this.canvas.width === width && this.canvas.height === height) return;

    this.canvas.width = width;
    this.canvas.height = height;
    this.computeFit();
    this.present();
  }

  private computeFit(): void {
    const cw = this.canvas.width;
    const ch = this.canvas.height;
    if (this.width === 0 || this.height === 0) {
      this.fit = { x: 0, y: 0, width: cw, height: ch };
      return;
    }
    // Seitenverhältnis wahren und zentrieren – wie computeScaledValues im Original.
    if (cw / ch >= this.width / this.height) {
      const height = ch;
      const width = (height * this.width) / this.height;
      this.fit = { x: (cw - width) / 2, y: 0, width, height };
    } else {
      const width = cw;
      const height = (width * this.height) / this.width;
      this.fit = { x: 0, y: (ch - height) / 2, width, height };
    }
  }

  private present(): void {
    if (!this.full) return;
    this.ctx.fillStyle = '#000';
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.imageSmoothingQuality = 'high';
    this.ctx.drawImage(this.full, this.fit.x, this.fit.y, this.fit.width, this.fit.height);
  }
}
